import { Component, Element, Event, EventEmitter, Method, Prop, h } from '@stencil/core';

/**
 * Modal Component - uses dialog element
 * @slot button - Button to open the modal
 * @slot modal - Content to be displayed in the modal
 */
@Component({
  tag: 'ad-modal',
  styleUrl: 'ad-modal.css',
  shadow: true,
})

export class AdessoModal {
    @Element() el: HTMLElement;
    /** Open state - set programmatically to control state */
    @Prop({reflect: true, mutable: true }) opened: boolean;
    YTVideo?: HTMLIFrameElement;
    video?: HTMLVideoElement;

    /** Event is emitted, when the modal opens */
    @Event({bubbles: true, composed: true}) adModalOpen: EventEmitter<string>;
     /** Event is emitted, when the modal closes */
    @Event({bubbles: true, composed: true}) adModalClose: EventEmitter<string>;


    /**
     * Open the Modal
     */
    @Method()
    async open() {
        this.onOpen();
    }

    /**
     * Close the Modal
     */
    @Method()
    async close() {
        this.onClose();
    }

    componentWillLoad() {
        document.addEventListener('keydown', (event) => {
            if(event.key === "Escape" && this.opened) {
                this.close();
            }
        });
    }

    componentWillRender() {
        if(!(this.YTVideo || this.video)) {
            this.getVideo();
        }
    }

    componentDidRender() {
        if(this.opened) {
            this.playVideo();
            this.el.shadowRoot.querySelector('dialog').showModal();
        } else {
            this.pauseVideo();
            this.el.shadowRoot.querySelector('dialog').close();
        }
    }

    /**
     *  Play Video, if exists
     */
    playVideo() {
		if(this.YTVideo) this.YTVideo.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*');
		if(this.video) this.video.play();
	}

    /**
     * Pause Video, if exists
     */
	pauseVideo() {
		if(this.YTVideo) this.YTVideo.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
		if(this.video) this.video.pause();
	}

    /**
     * get Video content, if directly slotted to the modal slot
     */
    getVideo() {
        if(!(this.YTVideo || this.video)) {
            const contentSlot : HTMLSlotElement = this.el.shadowRoot.querySelector('slot[name="modal"]') as HTMLSlotElement;
            const slottedContent : Element[] = contentSlot?.assignedElements();
            slottedContent?.forEach(e => {
                if(e instanceof HTMLIFrameElement) {
                    this.YTVideo = e;
                } else if (e instanceof HTMLVideoElement) {
                    this.video = e;
                } else if (e.querySelector('iframe')) {
                    this.YTVideo = e.querySelector('iframe');
                } else if (e.querySelector('video')) {
                    this.video = e.querySelector('video');
                }
            });
        }
    }

    onOpenClick(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.onOpen();
    }

    onClose() {
        document.body.style.removeProperty("overflow");
        this.opened = false;
        this.adModalClose.emit("Modal is closed");
    }

    onOpen() {
        document.body.style.overflow = "hidden";
        this.opened = true;
        this.adModalOpen.emit("Modal is opened");
    }

    onKeydownHandler(event : KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevents the default action
            this.onOpen();
        }
    }

    /**
     * Detect click outside the modal dimensions to close it
     * @param event 
     */
    onHandleClickOutside(event: MouseEvent) {
        event.stopPropagation();
        const dialog = this.el.shadowRoot.querySelector('dialog');
        const rect = dialog.getBoundingClientRect();
        const isInDialog = (rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX && event.clientX <= rect.left + rect.width);
        if (!isInDialog) {
            this.onClose();
        }	
    }

    render() {        
        return [
            <a onClick={this.onOpenClick.bind(this)} onKeyDown={this.onKeydownHandler.bind(this)}><slot name="button" /></a>,
            <dialog class="popup slide-in-blurred-top" onClick={this.onHandleClickOutside.bind(this)}>
                    <button class="close-btn" aria-label="Close" tabindex="0" onClick={this.onClose.bind(this)}>
                        <ad-icon icon="xmark-large"></ad-icon>
                    </button>
                    <div class="modal--content">
                        <slot name="modal" /> 
                    </div>
            </dialog>
        ]
      }
}